import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';

/**
 * useExamFullscreen — enforces fullscreen mode for exam quizzes via user clicks.
 *
 * Browsers require a user gesture to call requestFullscreen(). This hook attaches
 * a document-level click listener: any click on the page while not in fullscreen
 * triggers requestFullscreen(). When the user exits fullscreen (Esc, F11, etc.),
 * the next click anywhere re-enters. After `threshold` exits, auto-submits.
 *
 * Uses both the `fullscreenchange` event and a 1s polling fallback to detect exits
 * across all browsers (Firefox F11 doesn't fire fullscreenchange).
 *
 * @param {object} options
 * @param {boolean} options.enabled - Only active for exam quizzes
 * @param {boolean} options.loaded - Quiz data has finished loading
 * @param {function} options.onThresholdReached - Auto-submit after threshold exits
 * @param {number} options.threshold - Exits before auto-submit (default: 3)
 */
function useExamFullscreen({ enabled = false, loaded = false, onThresholdReached, threshold = 3 }) {
  const exitCountRef = useRef(0);
  const wasFullscreenRef = useRef(false);
  const onThresholdRef = useRef(onThresholdReached);
  onThresholdRef.current = onThresholdReached;

  const requestFs = useCallback(() => {
    try {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch(() => {
          // Silently ignore — user may have dismissed the browser prompt
        });
      }
    } catch {
      // Fullscreen API not available
    }
  }, []);

  useEffect(() => {
    if (!enabled || !loaded) return;

    let pollInterval = null;

    const handleExit = () => {
      exitCountRef.current += 1;
      const count = exitCountRef.current;
      const remaining = threshold - count;

      if (count >= threshold) {
        toast.error(
          'You exited fullscreen too many times! Submitting your exam...',
          { duration: 5000 }
        );
        onThresholdRef.current?.();
        return;
      }

      toast.warning(`Fullscreen required! (${count}/${threshold})`, {
        description: remaining > 0
          ? `${remaining} exit${remaining === 1 ? '' : 's'} left before auto-submission. Click anywhere to re-enter.`
          : 'Click anywhere to re-enter fullscreen.',
        duration: 4000,
      });
    };

    // --- Click listener: entry point for fullscreen (user gesture context) ---
    const handleClick = (e) => {
      if (!document.fullscreenElement) {
        requestFs();
      }
    };

    // --- Event-based detection ---
    const handleFullscreenChange = () => {
      const isNowFullscreen = !!document.fullscreenElement;
      if (wasFullscreenRef.current && !isNowFullscreen) {
        handleExit();
      }
      wasFullscreenRef.current = isNowFullscreen;
    };

    // --- Polling fallback (catches F11 in Firefox) ---
    pollInterval = setInterval(() => {
      const isFullscreen = !!document.fullscreenElement;
      if (wasFullscreenRef.current && !isFullscreen) {
        handleExit();
        wasFullscreenRef.current = false;
      } else if (isFullscreen) {
        wasFullscreenRef.current = true;
      }
    }, 1000);

    // Initial fullscreen request (first click will trigger since useEffect
    // can't — but the click listener handles it from the start)
    document.addEventListener('click', handleClick, true);
    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      clearInterval(pollInterval);
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      }
    };
  }, [enabled, loaded, requestFs, threshold]);
}

export default useExamFullscreen;